import React, { useState, useEffect } from 'react';
import './EditProductModal.css';

// Funzione helper per sanitizzare il nome della cartella
const sanitizeFolderName = (name) => {
  return name
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Rimuovi accenti
    .replace(/[^a-z0-9]+/g, '-') // Sostituisci caratteri speciali con -
    .replace(/-+/g, '-') // Rimuovi trattini multipli
    .replace(/^-|-$/g, '') // Rimuovi trattini iniziali/finali
    .substring(0, 50); // Limita lunghezza
};

const EditProductModal = ({ isOpen, onClose, onEditProduct, product, categories }) => {
  const [formData, setFormData] = useState({
    id: '',
    titolo: '',
    descrizione: '',
    prezzo: '',
    quantita: '0',
    id_categoria: '',
    attivo: true,
    foto: null,
    fotoPreview: null,
    fotoAggiuntive: [],
    folderName: null
  });
  const [existingImages, setExistingImages] = useState({
    main: null,
    additional: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.NODE_ENV === 'production' 
    ? '/shop/backend/api' 
    : 'http://efwypvu.cluster021.hosting.ovh.net/shop/backend/api';

  // Carica immagini esistenti quando si apre il modal
  useEffect(() => {
    const loadExistingImages = async () => {
      if (product && product.foto) {
        try {
          console.log('📸 Caricamento immagini esistenti per:', product.foto);
          const response = await fetch(`${API_BASE_URL}/get_images.php?folder=${product.foto}`);
          const data = await response.json();
          
          if (data.success) {
            setExistingImages({
              main: data.data.main_image,
              additional: data.data.additional_images || []
            });
            console.log('✅ Immagini caricate:', data.data);
          }
        } catch (err) {
          console.warn('⚠️ Errore caricamento immagini:', err);
        }
      } else {
        setExistingImages({ main: null, additional: [] });
      }
    };

    if (product) {
      setFormData({
        id: product.id_prodotto || '',
        titolo: product.titolo || '',
        descrizione: product.descrizione || '',
        prezzo: product.prezzo || '',
        quantita: (product.quantita ?? 0).toString(),
        id_categoria: product.id_categoria || '',
        attivo: product.attivo === 1 || product.attivo === '1' || product.attivo === true,
        foto: null,
        fotoPreview: null,
        fotoAggiuntive: [],
        folderName: product.foto || null
      });
      loadExistingImages();
    } else {
      setFormData({
        id: '',
        titolo: '',
        descrizione: '',
        prezzo: '',
        quantita: '0',
        id_categoria: '',
        attivo: true,
        foto: null,
        fotoPreview: null,
        fotoAggiuntive: [],
        folderName: null
      });
      setExistingImages({ main: null, additional: [] });
    }
    setError(null);
  }, [product, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file' && files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData({
          ...formData,
          [name]: file,
          fotoPreview: e.target.result
        });
      };
      reader.readAsDataURL(file);
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  const handleAdditionalImages = (e) => {
    const files = Array.from(e.target.files);
    const readers = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve({ file, preview: e.target.result });
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers).then(previews => {
      setFormData({
        ...formData,
        fotoAggiuntive: [...formData.fotoAggiuntive, ...previews].slice(0, 3) // Max 3 immagini aggiuntive
      });
    });
  };

  const removeAdditionalImage = (index) => {
    setFormData({
      ...formData,
      fotoAggiuntive: formData.fotoAggiuntive.filter((_, i) => i !== index)
    });
  };

  const removeExistingImage = async (imageData, isMain = false) => {
    if (!formData.folderName || !imageData.file_name) {
      console.error('Dati immagine mancanti');
      return;
    }

    const confirmDelete = window.confirm(`Vuoi eliminare questa immagine? Questa azione è irreversibile.`);
    if (!confirmDelete) return;

    try {
      console.log('🗑️ Eliminazione immagine:', imageData.file_name);
      
      const response = await fetch(`${API_BASE_URL}/delete_single_image.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          folder: formData.folderName,
          filename: imageData.file_name
        }),
      });

      const data = await response.json();
      console.log('📋 Risposta eliminazione immagine:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante l\'eliminazione dell\'immagine');
      }

      console.log('✅ Immagine eliminata con successo!');
      
      // Aggiorna lo stato locale rimuovendo l'immagine
      if (isMain) {
        setExistingImages({
          ...existingImages,
          main: null
        });
      } else {
        setExistingImages({
          ...existingImages,
          additional: existingImages.additional.filter(img => img.file_name !== imageData.file_name)
        });
      }
      
    } catch (err) {
      console.error('💥 Errore eliminazione immagine:', err);
      alert('Errore durante l\'eliminazione dell\'immagine: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validazioni
      if (!formData.titolo.trim()) {
        throw new Error('Il titolo è obbligatorio');
      }

      if (!formData.descrizione.trim()) {
        throw new Error('La descrizione è obbligatoria');
      }

      if (!formData.prezzo || parseFloat(formData.prezzo) <= 0) {
        throw new Error('Il prezzo deve essere maggiore di 0');
      }

      if (!formData.id_categoria) {
        throw new Error('La categoria è obbligatoria');
      }

      const quantityValue = parseInt(formData.quantita, 10);
      if (Number.isNaN(quantityValue) || quantityValue < 0) {
        throw new Error('La quantità deve essere maggiore o uguale a 0');
      }

      console.log('📸 Controllo nuove immagini...');
      
      // Usa sempre la cartella originale del prodotto (non creare una nuova)
      let folderName = formData.folderName;
      
      // Se il prodotto non aveva una cartella foto, creane una con il nome attuale
      if (!folderName && (formData.foto || formData.fotoAggiuntive.length > 0)) {
        folderName = sanitizeFolderName(formData.titolo);
      }
      
      // Upload nuove immagini se presenti (nella cartella esistente!)
      if (formData.foto && folderName) {
        console.log('📤 Upload nuova immagine principale nella cartella esistente:', folderName);
        const formDataImage = new FormData();
        formDataImage.append('image', formData.foto);
        formDataImage.append('product_name', folderName); // USA IL NOME DELLA CARTELLA ESISTENTE
        formDataImage.append('image_type', 'main');
        
        const uploadResponse = await fetch(`${API_BASE_URL}/upload_image.php`, {
          method: 'POST',
          body: formDataImage
        });
        
        const uploadData = await uploadResponse.json();
        
        if (!uploadData.success) {
          throw new Error(uploadData.error || 'Errore upload immagine principale');
        }
        
        console.log('✅ Nuova immagine principale caricata nella cartella esistente!');
      }
      
      // Upload nuove immagini aggiuntive se presenti (nella cartella esistente!)
      if (formData.fotoAggiuntive && formData.fotoAggiuntive.length > 0 && folderName) {
        console.log('📤 Upload', formData.fotoAggiuntive.length, 'nuove immagini aggiuntive nella cartella esistente:', folderName);
        
        for (let i = 0; i < formData.fotoAggiuntive.length; i++) {
          const imgData = formData.fotoAggiuntive[i];
          const formDataImage = new FormData();
          formDataImage.append('image', imgData.file);
          formDataImage.append('product_name', folderName); // USA IL NOME DELLA CARTELLA ESISTENTE
          formDataImage.append('image_type', 'additional');
          
          const uploadResponse = await fetch(`${API_BASE_URL}/upload_image.php`, {
            method: 'POST',
            body: formDataImage
          });
          
          const uploadData = await uploadResponse.json();
          
          if (!uploadData.success) {
            console.warn(`⚠️ Errore upload immagine ${i + 1}:`, uploadData.error);
          } else {
            console.log(`✅ Immagine aggiuntiva ${i + 1} caricata nella cartella esistente!`);
          }
        }
      }

      // Prepara i dati per l'invio
      const submitData = {
        id: parseInt(formData.id),
        titolo: formData.titolo.trim(),
        descrizione: formData.descrizione.trim(),
        prezzo: parseFloat(formData.prezzo),
        quantita: quantityValue,
        id_categoria: parseInt(formData.id_categoria),
        attivo: formData.attivo ? 1 : 0,
        foto: folderName // Aggiorna solo se sono state caricate nuove immagini
      };
      
      console.log('📝 Dati prodotto da modificare:', submitData);

      await onEditProduct(submitData);
    } catch (err) {
      console.error('💥 Errore:', err);
      setError(err.message || 'Errore durante la modifica del prodotto');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="product-modal-overlay" onClick={onClose}>
      <div className="product-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* X nell'angolo in alto a destra */}
        <button className="product-modal-close-button" onClick={onClose}>
          ×
        </button>
        
        <div className="product-modal-header">
          <h2>Modifica Prodotto</h2>
        </div>
        
        <div className="product-modal-body">
          <form onSubmit={handleSubmit} className="product-modal-form">
            <div className="form-layout">
              {/* Sezione Sinistra - Form */}
              <div className="form-left">
                <div className="form-group">
                  <label htmlFor="titolo">Titolo Prodotto *</label>
                  <input
                    type="text"
                    id="titolo"
                    name="titolo"
                    value={formData.titolo}
                    onChange={handleChange}
                    required
                    placeholder="Inserisci titolo prodotto"
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="descrizione">Descrizione *</label>
                  <textarea
                    id="descrizione"
                    name="descrizione"
                    value={formData.descrizione}
                    onChange={handleChange}
                    required
                    placeholder="Inserisci descrizione prodotto"
                    rows="4"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="prezzo">Prezzo (€) *</label>
                    <input
                      type="number"
                      id="prezzo"
                      name="prezzo"
                      value={formData.prezzo}
                      onChange={handleChange}
                      required
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="quantita">Quantità *</label>
                    <input
                      type="number"
                      id="quantita"
                      name="quantita"
                      value={formData.quantita}
                      onChange={handleChange}
                      required
                      min="0"
                      step="1"
                      placeholder="0"
                    />
                  </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="id_categoria">Categoria *</label>
                    <select
                      id="id_categoria"
                      name="id_categoria"
                      value={formData.id_categoria}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Seleziona categoria</option>
                      {categories.map((category) => (
                        <option key={category.id_categoria} value={category.id_categoria}>
                          {category.nome}
                        </option>
                      ))}
                    </select>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="attivo"
                      checked={formData.attivo}
                      onChange={handleChange}
                    />
                    <span className="checkbox-custom"></span>
                    Prodotto attivo
                  </label>
                  <small className="form-help">
                    I prodotti inattivi non saranno visibili nello shop
                  </small>
                </div>
              </div>

              {/* Sezione Destra - Immagini */}
              <div className="form-right">
                <div className="images-section">
                  <h3>Immagini Prodotto</h3>
                  
                  {/* Immagine Principale */}
                  <div className="main-image-container">
                    <label htmlFor="foto" className="main-image-upload">
                      {formData.fotoPreview ? (
                        <img src={formData.fotoPreview} alt="Preview" className="main-image-preview" />
                      ) : existingImages.main ? (
                        <>
                          <img src={existingImages.main.full_url} alt="Preview" className="main-image-preview" />
                          <button 
                            type="button"
                            className="remove-existing-image"
                            onClick={(e) => {
                              e.preventDefault();
                              removeExistingImage(existingImages.main, true);
                            }}
                            title="Elimina immagine"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </>
                      ) : (
                        <div className="main-image-placeholder">
                          <i className="fas fa-camera"></i>
                          <span>Immagine Principale</span>
                        </div>
                      )}
                    </label>
                    <input
                      type="file"
                      id="foto"
                      name="foto"
                      onChange={handleChange}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />
                  </div>

                  {/* Immagini Aggiuntive */}
                  <div className="additional-images">
                    <label htmlFor="fotoAggiuntive" className="additional-upload">
                      <i className="fas fa-plus"></i>
                      <span>Aggiungi Foto</span>
                    </label>
                    <input
                      type="file"
                      id="fotoAggiuntive"
                      onChange={handleAdditionalImages}
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                    />
                    
                    <div className="additional-images-grid">
                      {/* Immagini esistenti */}
                      {existingImages.additional.map((img, index) => (
                        <div key={`existing-${index}`} className="additional-image-item">
                          <img src={img.full_url} alt={`Existing ${index + 1}`} />
                          <button
                            type="button"
                            className="remove-image"
                            onClick={() => removeExistingImage(img, false)}
                            title="Elimina immagine"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                      
                      {/* Nuove immagini */}
                      {formData.fotoAggiuntive.map((image, index) => (
                        <div key={index} className="additional-image-item">
                          <img src={image.preview} alt={`Preview ${index + 1}`} />
                          <button
                            type="button"
                            className="remove-image"
                            onClick={() => removeAdditionalImage(index)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                      
                      {/* Placeholder vuoti */}
                      {Array.from({ length: 3 - existingImages.additional.length - formData.fotoAggiuntive.length }).map((_, index) => (
                        <div key={`empty-${index}`} className="additional-image-placeholder">
                          <i className="fas fa-image"></i>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="product-error-message">
                ⚠️ {error}
              </div>
            )}

            <div className="product-modal-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
                disabled={loading}
              >
                Annulla
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Modifica in corso...' : 'Modifica Prodotto'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProductModal;
